import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  getDocs,
  orderBy,
  limit,
  Timestamp,
  writeBatch,
  serverTimestamp,
  type Firestore,
  type DocumentData,
} from 'firebase/firestore';
import { db } from './config';
import type {
  UserProfile,
  UserTripHistory,
  CreateUserProfileRequest,
  UpdateUserProfileRequest,
  UpdateTravelPreferencesRequest,
  TravelPreferences,
} from '../../shared/types/user';
import debugLog from '@/lib/debug';

// ===== COLLECTION REFERENCES =====

const COLLECTIONS = {
  users: 'users',
  userTripHistory: 'userTripHistory',
} as const;

// ===== UTILITY FUNCTIONS =====

const ensureDb = (): Firestore => {
  if (!db) {
    throw new Error(
      'Firestore is not available. Please check your Firebase configuration and ensure Firestore is enabled in your Firebase project.'
    );
  }
  return db;
};

const convertTimestampToDate = (
  timestamp: Timestamp | Date | null | undefined
): Date | null => {
  if (!timestamp) {
    return null;
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return timestamp.toDate();
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const convertDatesToTimestamps = (data: any): any => {
  const converted = { ...data };

  // Remove undefined values to prevent Firestore errors
  Object.keys(converted).forEach(key => {
    if (converted[key] === undefined) {
      delete converted[key];
    }
  });

  // Convert Date objects to server timestamps for new documents
  if (converted.createdAt instanceof Date) {
    converted.createdAt = serverTimestamp();
  }
  if (converted.updatedAt instanceof Date) {
    converted.updatedAt = serverTimestamp();
  }
  if (converted.startDate instanceof Date) {
    converted.startDate = Timestamp.fromDate(converted.startDate);
  }
  if (converted.endDate instanceof Date) {
    converted.endDate = Timestamp.fromDate(converted.endDate);
  }

  return converted;
};

// ===== USER PROFILE OPERATIONS =====

export class UserProfileService {
  // Create a new user profile
  static async createUserProfile(
    userId: string,
    request: CreateUserProfileRequest
  ): Promise<UserProfile> {
    const database = ensureDb();

    const now = new Date();

    const userProfile: UserProfile = {
      userId,
      displayName: request.displayName,
      email: '', // Will be populated from Firebase Auth
      createdAt: now,
      updatedAt: now,
      isAdmin: false,
      travelPreferences: request.travelPreferences
        ? {
            homeZipCode: request.travelPreferences.homeZipCode || '',
            preferredAirports:
              request.travelPreferences.preferredAirports || [],
          }
        : undefined,
      profileCompletionStatus: (() => {
        const preferencesComplete =
          !!request.travelPreferences &&
          !!request.travelPreferences.homeZipCode &&
          request.travelPreferences.homeZipCode.trim().length > 0 &&
          Array.isArray(request.travelPreferences.preferredAirports) &&
          request.travelPreferences.preferredAirports.length >= 1 &&
          request.travelPreferences.preferredAirports.length <= 3;

        return {
          basicInfo: true,
          preferences: preferencesComplete,
          paymentInfo: false,
          completionPercentage: preferencesComplete ? 100 : 50,
        };
      })(),
      verification: {
        emailVerified: false,
        phoneVerified: false,
        identityVerified: false,
      },
    };

    // Create user profile document
    await setDoc(
      doc(database, COLLECTIONS.users, userId),
      convertDatesToTimestamps(userProfile)
    );

    return userProfile;
  }

  // Get user profile
  static async getUserProfile(userId: string): Promise<UserProfile | null> {
    const database = ensureDb();

    try {
      const docRef = doc(database, COLLECTIONS.users, userId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data() as DocumentData;
        return {
          ...data,
          createdAt: convertTimestampToDate(data.createdAt),
          updatedAt: convertTimestampToDate(data.updatedAt),
        } as UserProfile;
      }

      return null;
    } catch (error) {
      debugLog('debug', 'Error getting user profile:', error);
      throw error;
    }
  }

  // Update user profile
  static async updateUserProfile(
    userId: string,
    updates: UpdateUserProfileRequest
  ): Promise<void> {
    const database = ensureDb();

    try {
      const docRef = doc(database, COLLECTIONS.users, userId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      debugLog('debug', 'Error updating user profile:', error);
      throw error;
    }
  }

  // Update travel preferences specifically
  static async updateTravelPreferences(
    userId: string,
    preferences: UpdateTravelPreferencesRequest
  ): Promise<void> {
    const database = ensureDb();

    try {
      const docRef = doc(database, COLLECTIONS.users, userId);

      // Get current profile to merge with existing travelPreferences
      const currentProfile = await this.getUserProfile(userId);

      if (!currentProfile) {
        // If no profile exists, create one with basic info
        await this.createUserProfile(userId, {
          displayName: '',
          travelPreferences: {
            homeZipCode: preferences.homeZipCode || '',
            preferredAirports: preferences.preferredAirports || [],
          },
        });
        return;
      }

      const currentPrefs = currentProfile.travelPreferences;

      const updatedPrefs: TravelPreferences = {
        homeZipCode: preferences.homeZipCode || currentPrefs?.homeZipCode || '',
        preferredAirports:
          preferences.preferredAirports !== undefined
            ? preferences.preferredAirports
            : currentPrefs?.preferredAirports || [],
      };

      const airportCount = updatedPrefs.preferredAirports?.length ?? 0;
      const hasValidZipCode =
        updatedPrefs.homeZipCode && updatedPrefs.homeZipCode.trim().length > 0;
      const preferencesComplete =
        hasValidZipCode && airportCount >= 1 && airportCount <= 3;

      // Compute new completion percentage (basicInfo weight 50, preferences 50)
      const newCompletionPercentage =
        (currentProfile.profileCompletionStatus.basicInfo ? 50 : 0) +
        (preferencesComplete ? 50 : 0);

      // Use setDoc with merge to handle both create and update scenarios
      await setDoc(
        docRef,
        {
          travelPreferences: updatedPrefs,
          profileCompletionStatus: {
            ...currentProfile.profileCompletionStatus,
            preferences: preferencesComplete,
            completionPercentage: newCompletionPercentage,
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (error) {
      debugLog('debug', 'Error updating travel preferences:', error);
      throw error;
    }
  }

  // Delete user profile and all related data
  static async deleteUserProfile(userId: string): Promise<void> {
    const database = ensureDb();

    try {
      const batch = writeBatch(database);

      // Delete user profile document
      batch.delete(doc(database, COLLECTIONS.users, userId));

      // Delete user's trips subcollection
      const tripsQuery = query(
        collection(database, COLLECTIONS.users, userId, 'trips')
      );
      const tripsDocs = await getDocs(tripsQuery);
      tripsDocs.forEach(doc => {
        batch.delete(doc.ref);
      });

      await batch.commit();
    } catch (error) {
      debugLog('debug', 'Error deleting user profile:', error);
      throw error;
    }
  }

  // Update profile completion status
  static async updateProfileCompletion(userId: string): Promise<void> {
    const database = ensureDb();

    try {
      const profile = await this.getUserProfile(userId);
      if (!profile) return;

      const basicInfo = !!(profile.displayName && profile.email);
      const preferences =
        !!profile.travelPreferences &&
        !!profile.travelPreferences.homeZipCode &&
        profile.travelPreferences.homeZipCode.trim().length > 0 &&
        Array.isArray(profile.travelPreferences.preferredAirports) &&
        profile.travelPreferences.preferredAirports.length >= 1 &&
        profile.travelPreferences.preferredAirports.length <= 3;
      const paymentInfo = true; // Payment info is now optional - always considered complete

      let completionPercentage = 0;
      if (basicInfo) completionPercentage += 50; // Increased weight since payment is optional
      if (preferences) completionPercentage += 50; // Increased weight since payment is optional
      // Payment info no longer affects completion percentage

      await updateDoc(doc(database, COLLECTIONS.users, userId), {
        'profileCompletionStatus.basicInfo': basicInfo,
        'profileCompletionStatus.preferences': preferences,
        'profileCompletionStatus.paymentInfo': paymentInfo,
        'profileCompletionStatus.completionPercentage': completionPercentage,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      debugLog('debug', 'Error updating profile completion:', error);
      throw error;
    }
  }
}

// ===== USER TRIP HISTORY OPERATIONS =====

export class UserTripHistoryService {
  // Add trip to history
  static async addTripToHistory(tripHistory: UserTripHistory): Promise<void> {
    const database = ensureDb();

    try {
      // Use the user's trips subcollection
      const docRef = doc(
        database,
        COLLECTIONS.users,
        tripHistory.userId,
        'trips',
        tripHistory.tripId
      );
      await setDoc(docRef, convertDatesToTimestamps(tripHistory));
    } catch (error) {
      debugLog('debug', 'Error adding trip to history:', error);
      throw error;
    }
  }

  // Get user trip history
  static async getUserTripHistory(
    userId: string,
    limitCount: number = 10
  ): Promise<UserTripHistory[]> {
    const database = ensureDb();

    try {
      // Query the user's trips subcollection
      const q = query(
        collection(database, COLLECTIONS.users, userId, 'trips'),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const tripHistory: UserTripHistory[] = [];

      querySnapshot.forEach(doc => {
        const data = doc.data() as DocumentData;

        // Smart date mapping: try preferences structure first, then fallback to direct properties
        let startDate = null;
        let endDate = null;

        // First, try to get dates from trip preferences (new structure)
        if (data.preferences?.dates?.start && data.preferences?.dates?.end) {
          startDate = convertTimestampToDate(data.preferences.dates.start);
          endDate = convertTimestampToDate(data.preferences.dates.end);
        }
        // Fallback to direct properties (legacy structure)
        else if (data.startDate && data.endDate) {
          startDate = convertTimestampToDate(data.startDate);
          endDate = convertTimestampToDate(data.endDate);
        }

        tripHistory.push({
          ...data,
          tripId: doc.id, // Add the document ID as tripId
          userId: userId, // Ensure userId is included
          tripTitle: data.title || data.tripTitle || 'Untitled Trip', // Map title to tripTitle
          createdAt: convertTimestampToDate(data.createdAt) || new Date(),
          updatedAt: convertTimestampToDate(data.updatedAt) || new Date(),
          startDate: startDate || new Date(),
          endDate: endDate || new Date(),
        } as UserTripHistory);
      });

      return tripHistory;
    } catch (error) {
      debugLog('debug', 'Error getting user trip history:', error);
      throw error;
    }
  }

  // Update trip in history
  static async updateTripInHistory(
    userId: string,
    tripId: string,
    updates: Partial<UserTripHistory>
  ): Promise<void> {
    const database = ensureDb();

    try {
      // Use the user's trips subcollection
      const docRef = doc(database, COLLECTIONS.users, userId, 'trips', tripId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      debugLog('debug', 'Error updating trip in history:', error);
      throw error;
    }
  }

  // Delete trip from history
  static async deleteTripFromHistory(
    userId: string,
    tripId: string
  ): Promise<void> {
    const database = ensureDb();

    try {
      // Use the user's trips subcollection
      await deleteDoc(
        doc(database, COLLECTIONS.users, userId, 'trips', tripId)
      );
    } catch (error) {
      debugLog('debug', 'Error deleting trip from history:', error);
      throw error;
    }
  }
}

// ===== COMPLETE USER DATA SERVICE =====

export class UserDataService {
  // Get complete user data
  static async getCompleteUserData(userId: string) {
    try {
      const [profile, tripHistory] = await Promise.all([
        UserProfileService.getUserProfile(userId),
        UserTripHistoryService.getUserTripHistory(userId, 5),
      ]);

      return {
        profile,
        tripHistory,
      };
    } catch (error) {
      debugLog('debug', 'Error getting complete user data:', error);
      throw error;
    }
  }

  // Initialize user data with profile and defaults
  static async initializeUserData(
    userId: string,
    email: string,
    displayName: string,
    travelPreferences?: Partial<TravelPreferences>,
    additionalData?: {
      photoURL?: string;
      phoneNumber?: string;
      socialProvider?: string;
      providerId?: string;
    }
  ) {
    try {
      // Create user profile with embedded preferences and additional data
      const userProfile = await UserProfileService.createUserProfile(userId, {
        displayName,
        travelPreferences,
        profilePictureUrl: additionalData?.photoURL,
        phoneNumber: additionalData?.phoneNumber,
      });

      // Update the email field and any additional social provider info
      const updateData: Record<string, unknown> = { email };
      if (additionalData?.socialProvider) {
        updateData.socialProvider = additionalData.socialProvider;
        updateData.providerId = additionalData.providerId;
      }

      await UserProfileService.updateUserProfile(userId, updateData);

      return {
        profile: userProfile,
        tripHistory: [],
      };
    } catch (error) {
      debugLog('debug', 'Error initializing user data:', error);
      throw error;
    }
  }

  // Update profile completion status
  static async updateProfileCompletion(userId: string): Promise<void> {
    return UserProfileService.updateProfileCompletion(userId);
  }
}
